import { LockIcon } from "@phosphor-icons/react/dist/csr/Lock";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@volca/ui/components/ui/tooltip";
import { cn } from "@volca/ui/lib/utils";
import { LOCKED_EXPLANATION } from "./pay-period-lock";

interface PayPeriodLockedMarkProps {
  // Shows the word next to the glyph. Off in dense lists, where the glyph sits
  // in the row's reserved status slot and the word goes to screen readers.
  withLabel?: boolean;
  className?: string;
  iconClassName?: string;
}

/**
 * The locked mark: a lock glyph, never a badge. Locked is a modifier on the
 * period's identity, not one more status pill sitting beside the run status —
 * so it reads as a mark on the period, and the run's own badge keeps its
 * meaning.
 */
export const PayPeriodLockedMark = ({
  withLabel = false,
  className,
  iconClassName,
}: PayPeriodLockedMarkProps) => (
  <span
    className={cn(
      "inline-flex shrink-0 items-center gap-1.5 text-subtle",
      className,
    )}
  >
    <LockIcon aria-hidden="true" className={cn("h-3.5 w-3.5", iconClassName)} />
    {withLabel ? (
      <span className="text-xs font-normal text-subtle">Locked</span>
    ) : (
      <span className="sr-only">Locked</span>
    )}
  </span>
);

/**
 * The mark as it rides on the pay period title, where there is a reader who
 * wants to know why payroll cannot be rerun. The menu's disabled rerun item
 * carries the same reason inline, so this is a second route to it.
 */
export const PayPeriodLockedTitleMark = () => (
  <Tooltip>
    <TooltipTrigger
      render={
        <span tabIndex={0} className="inline-flex rounded-sm outline-hidden" />
      }
    >
      <PayPeriodLockedMark iconClassName="h-4.5 w-4.5" />
    </TooltipTrigger>
    {/* Plain text, so it takes the tooltip's own foreground colour. Below the
        glyph because the title sits at the top of the page, where an upward
        tooltip is clipped. */}
    <TooltipContent side="bottom" align="start" className="max-w-64">
      {LOCKED_EXPLANATION}
    </TooltipContent>
  </Tooltip>
);
